'use client';

import { useState } from 'react';
import {
  X,
  Search,
  FileText,
  Users,
  Target,
  Map,
  BarChart3,
  Layers,
  BookOpen,
  Check,
  MessageSquare,
  Route,
  Film,
  Code,
  Package,
  LayoutDashboard,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTemplates } from '@/hooks/use-templates';
import type { Template } from '@audra-flow/types';
import type { LucideIcon } from 'lucide-react';

// Map template type to Lucide icon
const TYPE_ICONS: Record<string, LucideIcon> = {
  GOAL: Target,
  INTERVIEW: MessageSquare,
  COMPETITOR_ANALYSIS: BarChart3,
  PERSONA: Users,
  USE_CASE: Layers,
  JOURNEY: Route,
  SERVICE_MAP: Map,
  STORYBOARD: Film,
  BASE_SPEC: FileText,
  TECHNICAL_SPEC: Code,
  STORY: BookOpen,
  RELEASE: Package,
  EVIDENCE: FileText,
  PROJECT_OVERVIEW: LayoutDashboard,
};

interface AddTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTemplate: (template: { id: string; name: string; type: string; category: string }) => void;
}

export function AddTemplateDialog({ isOpen, onClose, onAddTemplate }: AddTemplateDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const { data, isLoading, isError } = useTemplates({
    status: 'ACTIVE',
    pageSize: 50,
  });

  if (!isOpen) return null;

  const templates = data?.templates || [];
  const categories = ['All', 'Discovery', 'Definition', 'Design', 'Delivery'];

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (template.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === 'All' ||
                           template.category.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  const handleAddTemplate = () => {
    if (selectedTemplate) {
      onAddTemplate({
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        type: selectedTemplate.type,
        category: selectedTemplate.category,
      });
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'discovery':
        return 'bg-blue-50 text-blue-700';
      case 'definition':
        return 'bg-purple-50 text-purple-700';
      case 'design':
        return 'bg-pink-50 text-pink-700';
      case 'delivery':
        return 'bg-green-50 text-green-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-emerald-50 text-emerald-700';
      case 'DRAFT':
        return 'bg-yellow-50 text-yellow-700';
      case 'IN_REVIEW':
        return 'bg-blue-50 text-blue-700';
      case 'DEPRECATED':
        return 'bg-gray-100 text-gray-500';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-[14px] shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-medium text-gray-900">Add Template</h2>
            <p className="text-sm text-gray-500 mt-1">
              Choose a template to navigate to its artifact section
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-[10px] hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-[10px] text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mt-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category === 'All' ? null : category)}
                className={cn(
                  'px-4 py-2 rounded-[10px] text-sm font-medium transition-colors',
                  (selectedCategory === category || (!selectedCategory && category === 'All'))
                    ? 'bg-brand-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Template List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
              <span className="ml-2 text-gray-500">Loading templates...</span>
            </div>
          ) : isError ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-red-300 mx-auto mb-4" />
              <p className="text-gray-500">Failed to load templates</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No templates found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredTemplates.map((template) => {
                const Icon = TYPE_ICONS[template.type] || FileText;
                const isSelected = selectedTemplate?.id === template.id;

                return (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={cn(
                      'flex items-start gap-4 p-4 rounded-[10px] border transition-all text-left',
                      isSelected
                        ? 'border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    <div className={cn(
                      'w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0',
                      getCategoryColor(template.category)
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900">
                          {template.name}
                        </h3>
                        <span className="text-xs text-gray-400">
                          v{template.activeVersion}
                        </span>
                        {template.isDefault && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {template.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={cn(
                          'inline-block px-2 py-0.5 rounded-full text-xs font-medium',
                          getCategoryColor(template.category)
                        )}>
                          {template.category.charAt(0).toUpperCase() + template.category.slice(1)}
                        </span>
                        <span className={cn(
                          'inline-block px-2 py-0.5 rounded-full text-xs font-medium',
                          getStatusColor(template.status)
                        )}>
                          {template.status}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <p className="text-sm text-gray-500">
            {selectedTemplate
              ? `Selected: ${selectedTemplate.name}`
              : 'Select a template to add'
            }
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="h-10 px-4 rounded-[10px] border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddTemplate}
              disabled={!selectedTemplate}
              className={cn(
                'h-10 px-6 rounded-[10px] font-medium transition-colors',
                selectedTemplate
                  ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              )}
            >
              Add Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
